import { useMemo } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react-native';

import { CatFishPattern } from '@/components/ui';
import { colors, radius, typography } from '@/theme/tokens';
import { useAuth } from '@/stores/authStore';
import { routesForRole, type RouteDef } from '@/navigation/routes';

import logo from '@assets/brand/logo.jpeg';
import { APP_VERSION } from '@/version';

interface Props {
  current: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onNavigate: (name: string) => void;
}

const GROUP_LABELS: Record<RouteDef['group'], string> = {
  principal: 'Operación diaria',
  gestion: 'Gestión',
  sistema: 'Sistema',
};

export function Sidebar({ current, collapsed = false, onToggleCollapse, onNavigate }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const signOut = useAuth((s) => s.signOut);

  const grouped = useMemo(() => {
    if (!perfil) return [] as Array<[RouteDef['group'], RouteDef[]]>;
    const r = routesForRole(perfil.rol);
    const groups = new Map<RouteDef['group'], RouteDef[]>();
    r.forEach((route) => {
      const arr = groups.get(route.group) ?? [];
      arr.push(route);
      groups.set(route.group, arr);
    });
    return Array.from(groups.entries());
  }, [perfil]);

  const width = collapsed ? 80 : 264;

  return (
    <View
      style={{
        width,
        backgroundColor: colors.magenta[800],
        paddingVertical: 18,
        gap: 6,
        // @ts-ignore web only
        backgroundImage: `radial-gradient(400px 200px at 110% -10%, ${colors.magenta[500]}55, transparent 60%)`,
        alignSelf: 'stretch',
        position: 'relative',
        overflow: 'hidden',
        // @ts-ignore web only
        transitionProperty: 'width',
        // @ts-ignore web only
        transitionDuration: '200ms',
        // @ts-ignore web only
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <CatFishPattern variant="magenta" opacity={0.35} tileSize={300} />
      {/* Header sidebar: logo + botón collapse SIEMPRE arriba */}
      <View
        style={{
          paddingHorizontal: collapsed ? 0 : 18,
          paddingBottom: 16,
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: collapsed ? 'flex-start' : 'space-between',
          gap: 10,
        }}
      >
        {!collapsed ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
            <Image source={logo} style={{ width: 36, height: 36, borderRadius: 10 }} />
            <View>
              <Text
                style={{
                  color: colors.white,
                  fontFamily: typography.display.fontFamily,
                  fontSize: 18,
                  fontWeight: typography.weights.bold,
                  letterSpacing: -0.2,
                }}
              >
                CliniCAT
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, letterSpacing: 1 }}>MEDICINA FELINA</Text>
                <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, letterSpacing: 0.4 }}>v{APP_VERSION}</Text>
              </View>
            </View>
          </View>
        ) : (
          <Image source={logo} style={{ width: 36, height: 36, borderRadius: 10 }} />
        )}

        {onToggleCollapse ? (
          <Pressable
            onPress={onToggleCollapse}
            hitSlop={6}
            style={({ hovered }) => ({
              width: collapsed ? 36 : 30,
              height: collapsed ? 36 : 30,
              borderRadius: collapsed ? 12 : 10,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hovered ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.07)',
              marginTop: collapsed ? 6 : 0,
              // @ts-ignore web only
              cursor: 'pointer',
            })}
            accessibilityLabel={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {collapsed ? <PanelLeftOpen size={18} color={colors.white} /> : <PanelLeftClose size={16} color={colors.white} />}
          </Pressable>
        ) : null}
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 18, gap: 6 }}
        showsVerticalScrollIndicator={false}
      >
        {grouped.map(([group, items]) => (
          <View key={group} style={{ gap: 2 }}>
            {!collapsed ? (
              <Text
                style={{
                  color: 'rgba(255,255,255,0.42)',
                  fontSize: 10,
                  letterSpacing: 1.4,
                  fontWeight: typography.weights.bold,
                  paddingHorizontal: 18,
                  paddingTop: 14,
                  paddingBottom: 6,
                  textTransform: 'uppercase',
                }}
              >
                {GROUP_LABELS[group]}
              </Text>
            ) : (
              <View
                style={{
                  height: 1,
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  marginHorizontal: 18,
                  marginVertical: 8,
                }}
              />
            )}
            {items.map((route) => {
              const Icon = route.icon;
              const active = current === route.name;
              return (
                <Pressable
                  key={route.name}
                  onPress={() => onNavigate(route.name)}
                  style={({ hovered }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    gap: 12,
                    marginHorizontal: collapsed ? 12 : 10,
                    paddingHorizontal: collapsed ? 0 : 12,
                    paddingVertical: 10,
                    borderRadius: radius.lg,
                    backgroundColor: active
                      ? 'rgba(255,255,255,0.16)'
                      : hovered
                        ? 'rgba(255,255,255,0.07)'
                        : 'transparent',
                    // @ts-ignore web only
                    transitionProperty: 'background-color',
                    // @ts-ignore web only
                    transitionDuration: '160ms',
                    // @ts-ignore web only
                    cursor: 'pointer',
                  })}
                  accessibilityLabel={route.label}
                >
                  <Icon size={19} color={active ? colors.white : 'rgba(255,255,255,0.78)'} />
                  {!collapsed ? (
                    <View style={{ flex: 1 }}>
                      <Text
                        style={{
                          color: active ? colors.white : 'rgba(255,255,255,0.85)',
                          fontWeight: active ? typography.weights.bold : typography.weights.medium,
                          fontSize: 14,
                        }}
                        numberOfLines={1}
                      >
                        {route.label}
                      </Text>
                    </View>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        ))}
      </ScrollView>

      {/* Footer: perfil */}
      <View
        style={{
          marginHorizontal: collapsed ? 12 : 14,
          marginTop: 8,
          marginBottom: 14,
          padding: collapsed ? 6 : 10,
          borderRadius: radius['2xl'],
          backgroundColor: 'rgba(255,255,255,0.06)',
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <View
          style={{
            width: 34,
            height: 34,
            borderRadius: 17,
            backgroundColor: colors.magenta[500],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text style={{ color: colors.white, fontWeight: typography.weights.bold }}>
            {perfil?.nombre?.[0]?.toUpperCase() ?? '·'}
          </Text>
        </View>
        {!collapsed ? (
          <View style={{ flex: 1 }}>
            <Text
              style={{ color: colors.white, fontSize: 13, fontWeight: typography.weights.semibold }}
              numberOfLines={1}
            >
              {perfil?.nombre}
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, textTransform: 'capitalize' }}>
              {perfil?.rol}
            </Text>
          </View>
        ) : null}
        <Pressable
          onPress={signOut}
          hitSlop={8}
          style={({ hovered }) => ({
            width: 30,
            height: 30,
            borderRadius: 10,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: hovered ? 'rgba(255,255,255,0.14)' : 'transparent',
            // @ts-ignore web only
            cursor: 'pointer',
          })}
          accessibilityLabel="Cerrar sesión"
        >
          <LogOut size={15} color="rgba(255,255,255,0.85)" />
        </Pressable>
      </View>
    </View>
  );
}
